import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPatch, operationsClient } from '@/lib/api/client';
import { billingSettingsEndpoints } from '@/lib/api/endpoints';
import { paymentKeys, settingsKeys } from '@/lib/queryKeys';
import type { BillingSettings, BillingSettingsTab, BillingTabPatchMap } from '@/types/settings';

interface UpdateBillingTabVars<T extends BillingSettingsTab> {
  tab: T;
  patch: BillingTabPatchMap[T];
}

/**
 * PATCH a single Billing-settings tab. Optimistic update: we apply the
 * patch into the cached `BillingSettings` immediately so the form reflects
 * the new state without a roundtrip; on error we roll back.
 */
export function useUpdateBillingTab<T extends BillingSettingsTab>() {
  const queryClient = useQueryClient();

  return useApiMutation<
    BillingSettings,
    UpdateBillingTabVars<T>,
    { previous: BillingSettings | undefined }
  >({
    mutationFn: ({ tab, patch }) =>
      apiPatch<BillingSettings, BillingTabPatchMap[T]>(
        operationsClient,
        billingSettingsEndpoints.updateTab(tab),
        patch,
      ),
    onMutate: async ({ tab, patch }) => {
      const queryKey = settingsKeys.billingSettings();
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<BillingSettings>(queryKey);

      if (previous) {
        const optimistic: BillingSettings = {
          ...previous,
          [tab]: deepMergeTab(tab, previous[tab], patch),
        } as BillingSettings;
        queryClient.setQueryData(queryKey, optimistic);
      }

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(settingsKeys.billingSettings(), context.previous);
      }
    },
    onSuccess: (data, vars) => {
      queryClient.setQueryData(settingsKeys.billingSettings(), data);
      // Whether a check can be scanned is judged from these settings (the
      // switch itself, and ACH): a cached answer from before the save would
      // keep offering, or keep refusing, the scan until it went stale.
      if (vars.tab === 'paymentMethods') {
        void queryClient.invalidateQueries({ queryKey: paymentKeys.chequeScanAvailability() });
      }
      toast.success(`${humanLabel(vars.tab)} settings saved`);
    },
  });
}

function humanLabel(tab: BillingSettingsTab): string {
  switch (tab) {
    case 'invoices':
      return 'Invoices';
    case 'receipts':
      return 'Receipts';
    case 'deposits':
      return 'Deposits';
    case 'paymentMethods':
      return 'Payment methods';
    case 'creditMemos':
      return 'Credit memos';
    case 'qbo':
      return 'QuickBooks';
  }
}

/**
 * Mirror of the backend per-tab merge — only the invoices tab needs deep
 * merging because of the nested branding + reminders objects.
 */
function deepMergeTab<T extends BillingSettingsTab>(
  tab: T,
  current: BillingSettings[T],
  patch: BillingTabPatchMap[T],
): BillingSettings[T] {
  if (tab === 'invoices') {
    type Inv = BillingSettings['invoices'];
    type IncInv = BillingTabPatchMap['invoices'];
    const cur = current as Inv;
    const inc = patch as IncInv;
    const merged: Inv = {
      ...cur,
      ...inc,
      branding: {
        ...cur.branding,
        ...(inc.branding ?? {}),
      },
      reminders: {
        ...cur.reminders,
        ...(inc.reminders ?? {}),
      },
    };
    return merged as BillingSettings[T];
  }
  return { ...current, ...patch } as BillingSettings[T];
}
